import { isEqual } from 'lodash';
import React, { useCallback, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ProgressStep from 'src/components/ProgressStep/ProgressStep';
import AppTitle from 'src/components/Shared/AppTitle/AppTitle';
import {
  CANCEL_TRADE_CONFIRMATION_MODAL,
  openModal,
} from 'src/redux/modules/modals/modals.module';
import { setProgress } from 'src/redux/modules/progress';
import { getProgressCurrentStep } from 'src/redux/modules/progress/selectors';
import { scrollElementIntoFullView } from 'src/utils/dom-utils';
import { Step1Text, Step2Text, Step3Text } from './TitlesAndCopy';
import './appSellSteps.scss';
import {
  getIsStep1NextButtonDisabled,
  getSellTransactionIsValid,
} from '../../../redux/modules/sellTransaction/sellTransaction.selectors';
import {
  getValidationResultLoading,
  getResultsLoading,
} from '../../../redux/modules/executeRedemption/selectors';
import { DeliveryMethodForm } from '../../Sell/DeliveryMethodForm/DeliveryMethodForm';
import DeliveryMethodChosen from '../../Sell/DeliveryMethodChosen/DeliveryMethodChosen';
import SellFundList from '../../Sell/SellFundList/SellFundList';
import SellFundSummary from '../../Sell/SellFundSummary/SellFundSummary';
import SellReview from '../../Sell/SellReview/SellReview';
import { isCustomerWebViewEnabled } from 'src/redux/modules/authentication/authentication.module.selectors';
import { getRedemptionPayload } from 'src/redux/modules/executeRedemption/executeRedemption.selectors';
import {
  setValidationStart,
  setValidationFailed,
  setValidationSuccessful,
  validateSingleRedemption,
} from 'src/redux/modules/executeRedemption/executeRedemption.module';
import RASText from '../../Shared/RASText/RASText';
import APPText from '../../Shared/APPText/APPText';
import { ThunkDispatch } from 'redux-thunk';

export interface AppSellStepsProps {
  submitRedemption?;
  hasRasFundsOnly?;
  hasAppFundsOnly?;
}
export const AppSellSteps: React.FC<AppSellStepsProps> = ({
  submitRedemption,
  hasRasFundsOnly,
  hasAppFundsOnly,
}) => {
  const dispatch: ThunkDispatch<any, any, any> = useDispatch();
  const [shouldValidate, setShouldValidate] = useState(false);
  const customerWebViewEnabled: boolean = useSelector(isCustomerWebViewEnabled);
  const isStep1NextButtonDisabled: boolean = useSelector(
    getIsStep1NextButtonDisabled
  );
  const redemptionPayload: [] = useSelector(getRedemptionPayload);
  const [oldRedemptionPayload, setOldRedemptionPayload] =
    useState(redemptionPayload);
  const validationResultLoading = useSelector(getValidationResultLoading);
  const isLoading = useSelector(getResultsLoading);
  const currentStep = useSelector(getProgressCurrentStep);
  const sellTransactionIsValid = useSelector(getSellTransactionIsValid);
  const onClickScroll = useCallback(
    (step: number, element: string) => {
      dispatch(setProgress(step));
      scrollElementIntoFullView(element, 15);
    },
    [dispatch]
  );

  useEffect(() => {
    if (!isEqual(oldRedemptionPayload, redemptionPayload)) {
      setOldRedemptionPayload(redemptionPayload);
      setShouldValidate(true);
    }
  }, [redemptionPayload, oldRedemptionPayload]);

  function sellStepValidation() {
    setValidationStart();
    const transactionsValidationArray = redemptionPayload.map((transaction) => {
      return dispatch(validateSingleRedemption(JSON.stringify(transaction)));
    });

    Promise.all(transactionsValidationArray).then(
      () => {
        setValidationSuccessful();
        setShouldValidate(false);
        onClickScroll(3, 'fund-list-step');
      },
      (e) => {
        setValidationFailed(e);
      }
    );
  }

  return (
    <form className="animated fadeInUp" onSubmit={(e) => e.preventDefault()}>
      {hasRasFundsOnly && (
        <AppTitle
          mboxSubtitle="ret-aa-new-app-buy-subtitle"
          mboxTitle="ret-aa-new-app-buy-title"
          subtitle={
            <span>
              Withdraw from your <RASText shouldWrap /> account(s) and have the
              proceeds sent to you.
            </span>
          }
          title={
            <span>
              Withdraw from Your <RASText shouldWrap tradeMark /> account(s)
            </span>
          }
        />
      )}
      {hasAppFundsOnly && (
        <AppTitle
          mboxSubtitle="ret-aa-new-app-buy-subtitle"
          mboxTitle="ret-aa-new-app-buy-title"
          subtitle={
            <span>
              Withdraw from your{' '}
              <APPText plural registeredTradeMark shouldWrap /> account(s) and
              have the proceeds sent to you.
            </span>
          }
          title={
            <span>
              Withdraw from Your{' '}
              <APPText plural registeredTradeMark shouldWrap /> Account(s)
            </span>
          }
        />
      )}
      {!hasRasFundsOnly && !hasAppFundsOnly && (
        <AppTitle
          mboxSubtitle="ret-aa-new-app-buy-subtitle"
          mboxTitle="ret-aa-new-app-buy-title"
          subtitle={
            <span>
              Withdraw from your{' '}
              <APPText plural registeredTradeMark shouldWrap /> and/or{' '}
              <RASText shouldWrap tradeMark /> account(s) and have the proceeds
              sent to you.
            </span>
          }
          title={<span> Withdraw from Your Advisory Accounts </span>}
        />
      )}

      <ProgressStep
        expanded
        showCancelLink
        active={currentStep === 1}
        className="funding-target-step"
        clickable={currentStep > 1}
        complete={currentStep > 1}
        hasApp={!hasRasFundsOnly}
        hasRas={!hasAppFundsOnly}
        nextButtonDisabled={isStep1NextButtonDisabled}
        nextButtonLabel={<Step1Text.Button.Next />}
        step={1}
        title={<Step1Text.Title />}
        onCancelClick={() =>
          dispatch(openModal(CANCEL_TRADE_CONFIRMATION_MODAL))
        }
        onClickHeader={() => {
          currentStep > 1 && onClickScroll(1, 'funding-target-step');
        }}
        onNextClick={() => {
          onClickScroll(2, 'funding-target-step');
        }}
      >
        {currentStep === 1 ? (
          <DeliveryMethodForm />
        ) : (
          <DeliveryMethodChosen
            onEditClick={() => {
              onClickScroll(1, 'funding-target-step');
            }}
          />
        )}
      </ProgressStep>

      <ProgressStep
        expanded
        showCancelLink
        active={currentStep === 2}
        className="fund-list-step"
        clickable={
          currentStep > 2 || (currentStep !== 2 && sellTransactionIsValid)
        }
        complete={currentStep > 2}
        hasApp={!hasRasFundsOnly}
        hasRas={!hasAppFundsOnly}
        nextButtonClassName="e2e-step2-verify-submit"
        nextButtonDisabled={!sellTransactionIsValid}
        nextButtonLabel={<Step2Text.Button.Next />}
        nextButtonLoading={validationResultLoading}
        step={2}
        title={<Step2Text.Title />}
        onCancelClick={() =>
          dispatch(openModal(CANCEL_TRADE_CONFIRMATION_MODAL))
        }
        onClickHeader={() => {
          (currentStep > 2 || (currentStep !== 2 && sellTransactionIsValid)) &&
            !isStep1NextButtonDisabled &&
            onClickScroll(2, 'funding-target-step');
        }}
        onNextClick={() => {
          shouldValidate
            ? sellStepValidation()
            : onClickScroll(3, 'fund-list-step');
        }}
      >
        {currentStep === 2 ? (
          <SellFundList currentStep={currentStep} />
        ) : (
          <SellFundSummary
            clickable={sellTransactionIsValid}
            onEditClick={() => {
              onClickScroll(2, 'funding-target-step');
            }}
          />
        )}
      </ProgressStep>
      {/* <ErrorSlideIn
        msg={message}
        show={validationResultLoaded && !isValidRedemption}
      /> */}

      <ProgressStep
        showCancelLink
        active={currentStep === 3}
        className="review-step"
        clickable={currentStep > 3}
        expanded={currentStep === 3}
        hasApp={!hasRasFundsOnly}
        hasRas={!hasAppFundsOnly}
        nextButtonDisabled={customerWebViewEnabled || isLoading}
        nextButtonLabel={<Step3Text.Button.Next />}
        step={3}
        title={<Step3Text.Title />}
        onCancelClick={() =>
          dispatch(openModal(CANCEL_TRADE_CONFIRMATION_MODAL))
        }
        onClickHeader={() => {
          currentStep >= 3 && onClickScroll(3, 'fund-list-step');
        }}
        onNextClick={submitRedemption}
      >
        {currentStep === 3 ? <SellReview /> : null}
      </ProgressStep>
    </form>
  );
};
